import { getCountryConfig, DEFAULT_COUNTRY, isValidCountryCode } from '../constants/countryConfig';

/**
 * Extract country code from URL parameters
 * @param {string} search - URL search string (e.g., '?country=PK&userId=123')
 * @returns {string} - Country code or default country
 */
export const getCountryFromURL = (search) => {
  const urlParams = new URLSearchParams(search);
  const country = urlParams.get('country');
  
  if (country && isValidCountryCode(country.toUpperCase())) {
    return country.toUpperCase();
  }
  
  return DEFAULT_COUNTRY;
};

/**
 * Format currency based on country configuration
 * @param {number|string} amount - Amount to format
 * @param {string} countryCode - Country code (PK, US, NZ, UK)
 * @param {object} options - Additional formatting options
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (amount, countryCode = DEFAULT_COUNTRY, options = {}) => {
  const config = getCountryConfig(countryCode);
  const { currency } = config;
  
  // Handle null, undefined, or empty values
  if (amount === null || amount === undefined || amount === '') {
    return `${currency.symbol}0.${'0'.repeat(currency.decimalPlaces)}`;
  }
  
  // Convert to number if it's a string
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  // Handle invalid numbers
  if (isNaN(numAmount)) {
    return `${currency.symbol}0.${'0'.repeat(currency.decimalPlaces)}`;
  }
  
  // Apply decimal places
  const decimalPlaces = options.decimalPlaces !== undefined ? options.decimalPlaces : currency.decimalPlaces;
  const formattedAmount = numAmount.toFixed(decimalPlaces);
  
  // Add thousand separators
  const parts = formattedAmount.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, currency.thousandSeparator);
  
  const finalAmount = parts.join(currency.decimalSeparator);
  
  // Return formatted currency
  return `${currency.symbol}${finalAmount}`;
};

/**
 * Get currency symbol for a country
 * @param {string} countryCode - Country code
 * @returns {string} - Currency symbol
 */
export const getCurrencySymbol = (countryCode = DEFAULT_COUNTRY) => {
  const config = getCountryConfig(countryCode);
  return config.currency.symbol;
};

/**
 * Get currency code for a country
 * @param {string} countryCode - Country code
 * @returns {string} - Currency code (USD, GBP, etc.)
 */
export const getCurrencyCode = (countryCode = DEFAULT_COUNTRY) => {
  const config = getCountryConfig(countryCode);
  return config.currency.code;
};

/**
 * Format date based on country configuration
 * @param {Date|string} date - Date to format
 * @param {string} countryCode - Country code
 * @returns {string} - Formatted date string
 */
export const formatDate = (date, countryCode = DEFAULT_COUNTRY) => {
  const config = getCountryConfig(countryCode);
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (!dateObj || isNaN(dateObj.getTime())) {
    return '';
  }
  
  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const year = dateObj.getFullYear();
  
  switch (config.dateFormat) {
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`;
    case 'DD-MM-YYYY':
      return `${day}-${month}-${year}`;
    case 'DD/MM/YYYY':
    default:
      return `${day}/${month}/${year}`;
  }
};

/**
 * Format number based on country configuration
 * @param {number} number - Number to format
 * @param {string} countryCode - Country code
 * @param {number} decimalPlaces - Number of decimal places
 * @returns {string} - Formatted number string
 */
export const formatNumber = (number, countryCode = DEFAULT_COUNTRY, decimalPlaces = 2) => {
  const config = getCountryConfig(countryCode);
  const { numberFormat } = config;
  
  if (isNaN(number)) {
    return '0';
  }
  
  const formattedNumber = number.toFixed(decimalPlaces);
  const parts = formattedNumber.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, numberFormat.thousandSeparator);
  
  return parts.join(numberFormat.decimalSeparator);
};

/**
 * Get regional divisions (states, provinces, counties) for a country
 * @param {string} countryCode - Country code
 * @returns {Array} - Array of regional divisions
 */
export const getRegionalDivisions = (countryCode = DEFAULT_COUNTRY) => {
  const config = getCountryConfig(countryCode);
  const { fields } = config;
  
  if (fields.showStates && fields.states) {
    return fields.states;
  }
  if (fields.showProvinces && fields.provinces) {
    return fields.provinces;
  }
  if (fields.showRegions && fields.regions) {
    return fields.regions;
  }
  if (fields.showCounties && fields.counties) {
    return fields.counties;
  }
  
  return [];
};

/**
 * Get the label for regional divisions (State, Province, County, etc.)
 * @param {string} countryCode - Country code
 * @returns {string} - Label for regional division
 */
export const getRegionalDivisionLabel = (countryCode = DEFAULT_COUNTRY) => {
  const config = getCountryConfig(countryCode);
  const { fields } = config;
  
  if (fields.showStates) return 'State';
  if (fields.showProvinces) return 'Province';
  if (fields.showRegions) return 'Region';
  if (fields.showCounties) return 'County';
  
  return 'Region';
};

/**
 * Check if a country uses states
 * @param {string} countryCode - Country code
 * @returns {boolean} - True if country uses states
 */
export const countryUsesStates = (countryCode = DEFAULT_COUNTRY) => {
  const config = getCountryConfig(countryCode);
  return config.fields.showStates || false;
};

/**
 * Get time format for a country (12h or 24h)
 * @param {string} countryCode - Country code
 * @returns {string} - Time format
 */
export const getTimeFormat = (countryCode = DEFAULT_COUNTRY) => {
  const config = getCountryConfig(countryCode);
  return config.timeFormat;
};

/**
 * Parse currency input and return numeric value
 * @param {string} currencyString - Currency string (e.g., '$123.45')
 * @param {string} countryCode - Country code
 * @returns {number} - Numeric value
 */
export const parseCurrency = (currencyString, countryCode = DEFAULT_COUNTRY) => {
  if (!currencyString) return 0;
  
  const config = getCountryConfig(countryCode);
  const { currency } = config;
  
  // Remove currency symbol and thousand separators
  let cleanString = currencyString.toString()
    .replace(currency.symbol, '')
    .replace(new RegExp(`\\${currency.thousandSeparator}`, 'g'), '')
    .trim();
  
  // Replace decimal separator with standard dot if different
  if (currency.decimalSeparator !== '.') {
    cleanString = cleanString.replace(currency.decimalSeparator, '.');
  }
  
  const parsed = parseFloat(cleanString);
  return isNaN(parsed) ? 0 : parsed;
};